import React, { FC, useMemo, useState } from 'react';
import cx from 'classnames';
import { isSameDay } from 'date-fns';
import moment from 'moment-timezone';
// redux
import { useSelector } from 'react-redux';
import useThunkDispatch from '../../../../../../core/customHooks/useThunkDispatch';
import { selectorGetQuotesAll } from 'redux/dailyQuotes-service/selector';
import { createDailyQuote, deleteDailyQuote } from 'redux/dailyQuotes-service/actions';
// hooks & constants
import { useLocales } from 'hooks/locales';
import { SUPPORTED_LANGUAGES } from 'core/constants';
// components
import { ImportedQuotesTable } from './ImportedQuotesTable';
import ButtonDefault from '../../../../Buttons/ButtonsDefault';
import { UploadedQuote, UploadQuotesInput } from './UploadQuotesInput';
import { validateUploadedQuotesList } from './UploadQuotesInput/helpers';
import Loader from '../../../../Loader';
// types
import { QuoteLocalizedRequest, QuoteRequest } from '@joc/api-gateway/lib/api-client';
// styles
import styles from './ImportQuotesTab.module.scss';

interface IImportQuoteTabProps {
	onClose: () => void;
	isEditing?: boolean;
}

export const ImportQuotesTab: FC<IImportQuoteTabProps> = ({ onClose, isEditing = false }) => {
	const dispatch = useThunkDispatch();
	const { localeIdByKey } = useLocales();

	const dailyQuotes = useSelector(selectorGetQuotesAll);

	const [uploadedQuotes, setUploadedQuotes] = useState<Array<UploadedQuote>>([]);
	const [errorsCount, setErrorsCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);

	const sortedQuotes = useMemo(() => {
		return [...uploadedQuotes].sort((a, b) => {
			const timeA = a.startDate && !Number.isNaN(a.startDate.getTime()) ? a.startDate.getTime() : 0;
			const timeB = b.startDate && !Number.isNaN(b.startDate.getTime()) ? b.startDate.getTime() : 0;
			return timeA - timeB;
		});
	}, [uploadedQuotes]);

	const handleInput = (quotes: Array<UploadedQuote>) => setUploadedQuotes(quotes);

	const handleImport = async () => {
		setIsLoading(true);
		try {
			await Promise.all(
				sortedQuotes.map(async uploadedQuote => {
					if (!uploadedQuote.startDate) return;
					const quotesWithSameDate = dailyQuotes.filter(quote =>
						isSameDay(new Date(quote.startDate), uploadedQuote.startDate!)
					);

					return Promise.all(quotesWithSameDate.map(async quote => dispatch(deleteDailyQuote(quote))));
				})
			);

			const requests: Array<QuoteRequest> = sortedQuotes.map(quote => {
				const quoteLocalized: QuoteLocalizedRequest[] = [];

				SUPPORTED_LANGUAGES.forEach(lang => {
					const text = (quote.messages?.[lang.key] || '').trim();
					if (text) {
						quoteLocalized.push(
							QuoteLocalizedRequest.fromJS({
								localeId: localeIdByKey[lang.key],
								messageLocalized: text,
							})
						);
					}
				});

				return QuoteRequest.fromJS({
					quoteLocalized,
					startDate: moment(quote.startDate)
						.utcOffset(0, true) // Convert back to UTC
						.toISOString(),
				});
			});

			await dispatch(createDailyQuote(requests));
			onClose();
		} finally {
			setIsLoading(false);
		}
	};

	const downloadTemplate = () => {
		const templateFileLink = `${process.env.PUBLIC_URL}/assets/documents/importDailyQuotesTemplate.xlsx`;

		fetch(templateFileLink)
			.then(resp => (resp.status === 200 ? resp.blob() : Promise.reject(new Error('error'))))
			.then(blob => {
				const url = window.URL.createObjectURL(blob);

				const a = document.createElement('a');
				a.style.display = 'none';
				a.href = url;
				a.download = 'daily_quotes_template.xlsx';

				document.body.appendChild(a);
				a.click();

				window.URL.revokeObjectURL(url);
			});
	};

	const handleQuoteDelete = async (quoteIndex: number) => {
		const remaining = sortedQuotes.filter((_, index) => index !== quoteIndex);
		const { validatedQuotes, errorsCount: nextErrorsCount } = await validateUploadedQuotesList(remaining);
		setErrorsCount(nextErrorsCount);
		setUploadedQuotes(validatedQuotes);
	};

	const handleClearAll = () => {
		setErrorsCount(0);
		setUploadedQuotes([]);
	};

	return (
		<div className={styles.wrap}>
			<UploadQuotesInput handleInput={handleInput} setErrorsCount={setErrorsCount} />

			<button className={styles.templateDownload} onClick={downloadTemplate}>
				Download list template
			</button>

			{uploadedQuotes.length ? (
				<>
					<div className={styles.tableHeader}>
						<span>
							Detected {uploadedQuotes.length} Daily Quotes
							{errorsCount ? (
								<>
									{', '}
									<span style={{ color: '#D00074' }}>{`but ${errorsCount} have errors.`}</span>
								</>
							) : (
								<></>
							)}
						</span>

						<button title="Clear all" className={styles.clearAllButton} onClick={handleClearAll}>
							Clear all
						</button>
					</div>

					<ImportedQuotesTable quotes={sortedQuotes} handleQuoteDelete={handleQuoteDelete} />
				</>
			) : null}

			<div className={styles.footer}>
				<ButtonDefault
					classList={['secondary', 'lg']}
					parentClassName={styles.footer_button}
					title="Cancel"
					clickHandler={onClose}
				/>
				{isLoading ? (
					<Loader loadProps={{ disableCenterStyle: true, disableMargin: true }} />
				) : (
					<ButtonDefault
						classList={['primary', 'lg']}
						parentClassName={cx(styles.footer_button, {
							[styles.disable_submit]: !uploadedQuotes.length || !!errorsCount,
						})}
						title={isEditing ? 'Save' : 'Create'}
						disabled={!uploadedQuotes.length || !!errorsCount}
						clickHandler={handleImport}
					/>
				)}
			</div>
		</div>
	);
};
